import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import { getOptimizedImageUrl } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';

export default function ServicePage() {
  const { id } = useParams();
  const { lang, t, services, allServices } = useLanguage();

  // Find service in current language list
  let service = services.find(s => s.id === id);

  // Intelligent slug mapping and redirection
  if (!service) {
    const otherLang = lang === 'da' ? 'en' : 'da';
    const otherService = allServices[otherLang].find(s => s.id === id);
    if (otherService) {
      const index = allServices[otherLang].findIndex(s => s.id === id);
      const correctService = allServices[lang][index];
      if (correctService) {
        const correctPath = lang === 'da' 
          ? `/ydelser/${correctService.id}` 
          : `/en/services/${correctService.id}`;
        return <Navigate to={correctPath} replace />;
      }
    }
    return <Navigate to={lang === 'da' ? '/' : '/en'} replace />;
  }

  const breadcrumbs = lang === 'da'
    ? [
        { name: 'Forside', href: '/' },
        { name: 'Ydelser', href: '/ydelser' },
        { name: service.title, href: `/ydelser/${service.id}` }
      ]
    : [
        { name: 'Home', href: '/en' },
        { name: 'Services', href: '/en/services' },
        { name: service.title, href: `/en/services/${service.id}` }
      ];

  const introParagraphs = service.fullDescription.split('\n\n');

  return (
    <div className="bg-white">
      <SEO 
        title={service.title} 
        description={service.shortDescription}
        ogImage={service.image}
      />
      <SchemaMarkup type="service" data={service} />
      <SchemaMarkup type="breadcrumbs" data={breadcrumbs} />

      <Hero
        title={service.title}
        subtitle={service.shortDescription}
        image={service.image}
        showCta={false}
      />

      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          <div className="lg:w-2/3">
            <Link 
              to={lang === 'da' ? '/' : '/en'} 
              className="inline-flex items-center gap-2 text-blue-700 font-bold text-xs uppercase tracking-widest mb-8 hover:-translate-x-1 transition-transform"
            >
              <ArrowLeft size={16} /> {t('backToHome')}
            </Link>
            <p className="text-sm font-bold text-blue-700 uppercase tracking-[0.3em] mb-4">
              {t('serviceDetailPage.tag')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 uppercase mb-8">{service.title}</h2>
            <div className="text-slate-600 max-w-none border-l-4 border-blue-700 pl-6 mb-12">
              {introParagraphs.map((paragraph, idx) => (
                <p key={idx} className="mb-6 last:mb-0 leading-relaxed text-lg">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="prose prose-lg text-slate-600 max-w-none">
              {service.bodyParagraphs.map((paragraph, idx) => (
                <p key={idx} className="mb-6 leading-relaxed text-lg">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
              {service.features.map((feature, index) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4 p-6 bg-slate-50 border border-slate-100"
                >
                  <CheckCircle2 className="text-blue-700 flex-shrink-0" size={24} />
                  <span className="font-bold text-slate-900 uppercase tracking-wide text-xs">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <aside className="lg:w-1/3 w-full space-y-8 lg:sticky lg:top-32">
            <div className="bg-slate-900 p-10 text-white">
              <h3 className="text-xl font-bold uppercase tracking-widest mb-6 border-b border-slate-700 pb-4">
                {t('serviceDetailPage.contactTitle')}
              </h3>
              <p className="text-slate-400 mb-8 leading-relaxed">
                {service.contactText}
              </p>
              <Link
                to={lang === 'da' ? '/kontakt' : '/en/contact'}
                className="block w-full bg-blue-700 text-white text-center py-4 uppercase font-bold tracking-widest text-sm hover:bg-blue-800 transition-colors"
              >
                {t('serviceDetailPage.contactBtn')}
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
